import { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { comics } from "@/mocks/comics";

type TabKey = "hot" | "weekly" | "new";

const tabs: { key: TabKey; label: string }[] = [
  { key: "hot", label: "人气榜" },
  { key: "weekly", label: "周榜" },
  { key: "new", label: "新作榜" },
];

export default function Rankings() {
  usePageMeta({
    title: "羞羞漫画韩国 - 韩国漫画实时榜单（人气榜、周榜、新作榜）",
    description:
      "羞羞漫画韩国实时榜单推荐，涵盖人气榜、周榜与新作榜，按热度与口碑客观排序，帮你快速找到当前最值得阅读的韩国漫画。",
  });
  const [tab, setTab] = useState<TabKey>("hot");

  const list = (() => {
    if (tab === "new") return [...comics].filter((c) => c.isNew || c.chapters <= 78).sort((a, b) => b.rating - a.rating);
    if (tab === "weekly") return [...comics].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0) || b.views.localeCompare(a.views, "zh-Hans-CN"));
    return [...comics].sort((a, b) => Number(b.views.replace("万", "").replace("亿", "")) - Number(a.views.replace("万", "").replace("亿", "")));
  })();

  const tagline: Record<TabKey, string> = {
    hot: "按累计热度综合排序，最受读者欢迎的作品都在这里。",
    weekly: "本周讨论与更新热度最高的作品，追更别错过。",
    new: "新作与新番精选，抢先体验最新上架的韩国漫画。",
  };

  const medal = ["bg-accent-500 text-background-50", "bg-secondary-400 text-background-50", "bg-primary-300 text-primary-950"];

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">实时榜单</span>
          </nav>
          <h1 className="mt-4 font-display text-3xl font-black text-foreground-950 md:text-4xl">
            实时榜单
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">{tagline[tab]}</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10 md:px-6">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="榜单切换">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={tab === t.key}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                tab === t.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <ol className="mt-8 space-y-3">
          {list.map((comic, i) => (
            <li key={comic.id}>
              <Link
                to={`/detail/${comic.id}`}
                className="group flex items-center gap-4 rounded-xl bg-background-100 p-3 transition-all hover:bg-background-200/60"
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg font-display text-lg font-black ${
                    i < 3 ? medal[i] : "bg-background-50 text-foreground-600 ring-1 ring-background-200/70"
                  }`}
                >
                  {i + 1}
                </span>
                <img
                  src={comic.cover}
                  alt={`${comic.title} 封面`}
                  title={`${comic.title} · 韩国${comic.category}漫画`}
                  className="h-20 w-14 shrink-0 rounded-lg object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-display text-base font-bold text-foreground-950 group-hover:text-primary-600">
                      {comic.title}
                    </p>
                    <span className="rounded-md bg-background-50 px-2 py-0.5 text-[11px] text-foreground-500 ring-1 ring-background-200/70">
                      {comic.category}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-600">{comic.intro}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line" aria-hidden="true"></i>
                      {comic.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
                      {comic.rating.toFixed(1)}
                    </span>
                    <span>{comic.chapters}话</span>
                    <span className="text-foreground-400">{comic.updateAt}</span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}